import { API_BASE_URL, getAccessToken, refreshSession } from './api';

/**
 * Studio WebSocket (spec D5 + #22/#27 + WS1 + WS2) — one shared connection per TAB carrying:
 *   job frames (build progress + Sparky narration), collab presence/strokes/pins, the review
 *   handshake, and app-room relay frames for the preview bridge.
 *
 * WS2 — TRANSPORT OWNERSHIP. Everything mutable lives in a per-tab holder on `window`, not in module
 * scope, and the module disposes itself on hot replacement.
 *
 * Why that matters more than any individual bug: this file is a singleton with self-sustaining
 * timers, and any environment that re-instantiates modules (Vite HMR, most obviously) produced
 * SEVERAL live copies in one tab. Each kept its own socket, its own reconnect loop, and whatever
 * heartbeat rule existed when it was born — so they churned connections, rejoined projects the user
 * had closed twenty minutes earlier, and pushed server rooms to five members for a single tab. Four
 * separate transport bugs were fixed before it became clear they were all the same missing thing:
 * nobody owned the lifecycle. A holder plus a dispose hook makes that class impossible rather than
 * repeatedly repaired.
 */

export type Frame = Record<string, unknown> & { t?: string };
type Handler = (frame: Frame) => void;

const PING_EVERY_MS = 25_000;
/** two unanswered pings — never elapsed time, which a quiet build or a throttled tab both fake */
const MAX_MISSED_PONGS = 2;

interface Transport {
  ws: WebSocket | null;
  connecting: boolean;
  backoff: number;
  currentProject: { projectId: string; name: string } | null;
  handlers: Set<Handler>;
  connectionListeners: Set<(open: boolean) => void>;
  lastReportedOpen: boolean;
  missedPongs: number;
  lastFrameAt: number;
  rejoinAfterRefresh: boolean;
  heartbeat: number | null;
  retryTimer: number | null;
}

const KEY = '__cbxTransport';

/** Adopt the tab's transport if one exists; only the first module copy ever creates it. */
function holder(): Transport {
  const w = window as unknown as Record<string, Transport | undefined>;
  const existing = w[KEY];
  if (existing) return existing;
  const created: Transport = {
    ws: null,
    connecting: false,
    backoff: 500,
    currentProject: null,
    handlers: new Set(),
    connectionListeners: new Set(),
    lastReportedOpen: false,
    missedPongs: 0,
    lastFrameAt: 0,
    rejoinAfterRefresh: false,
    heartbeat: null,
    retryTimer: null,
  };
  w[KEY] = created;
  return created;
}

function log(event: string, detail?: unknown): void {
  // eslint-disable-next-line no-console
  console.info(`[cbx:ws] ${event}`, detail ?? '');
}

function reportConnection(): void {
  const t = holder();
  const open = Boolean(t.ws && t.ws.readyState === 1);
  if (open === t.lastReportedOpen) return;
  t.lastReportedOpen = open;
  t.connectionListeners.forEach(listener => {
    try {
      listener(open);
    } catch {
      /* a listener must not break the socket */
    }
  });
}

function wsUrl(): string {
  const token = getAccessToken();
  const base = API_BASE_URL.replace(/^http/, 'ws');
  return `${base}/box/ws?token=${encodeURIComponent(token ?? '')}`;
}

function connect(): void {
  const t = holder();
  if (t.connecting || (t.ws && t.ws.readyState <= 1)) return;

  if (!getAccessToken()) {
    /* Single-flight across the whole wait: without holding `connecting`, every caller that wanted a
       socket before auth resolved queued its own attempt and they all opened one. */
    t.connecting = true;
    log('waiting for auth — retrying in 750ms');
    t.retryTimer = window.setTimeout(() => {
      holder().connecting = false;
      connect();
    }, 750);
    return;
  }
  t.connecting = true;

  let socket: WebSocket;
  try {
    socket = new WebSocket(wsUrl());
  } catch (err) {
    /* A throwing constructor used to latch `connecting` true forever, so nothing ever retried. */
    t.connecting = false;
    log('constructor threw — retrying', err);
    t.retryTimer = window.setTimeout(connect, t.backoff);
    t.backoff = Math.min(t.backoff * 2, 15_000);
    return;
  }

  socket.onopen = () => {
    const h = holder();
    h.connecting = false;
    h.backoff = 500;
    if (h.ws && h.ws !== socket) {
      log('closing superseded socket');
      try {
        h.ws.close();
      } catch {
        /* already gone */
      }
    }
    h.ws = socket;
    h.lastFrameAt = Date.now();
    h.missedPongs = 0;
    log('open — joining', h.currentProject?.projectId ?? '(no project yet)');
    if (h.currentProject) socket.send(JSON.stringify({ t: 'join', ...h.currentProject }));
    reportConnection();
  };

  socket.onmessage = event => {
    const h = holder();
    h.lastFrameAt = Date.now();
    h.missedPongs = 0; // any frame at all proves the peer is answering
    let frame: Frame;
    try {
      frame = JSON.parse(String(event.data));
    } catch {
      return;
    }
    if (frame.t === 'pong') return; // liveness only; nothing downstream cares

    /* The upgrade succeeds even for a token the server will not verify — it just marks the socket a
       guest, whose join is refused. Refresh once and reconnect rather than holding a socket that can
       never deliver a job frame. */
    if (frame.t === 'join-denied') {
      log('join denied — token rejected; refreshing');
      if (h.rejoinAfterRefresh) return;
      h.rejoinAfterRefresh = true;
      void refreshSession().finally(() => {
        holder().rejoinAfterRefresh = false;
        try {
          socket.close();
        } catch {
          /* already closed */
        }
      });
      return;
    }

    h.handlers.forEach(handler => {
      try {
        handler(frame);
      } catch {
        /* one bad handler must not break the rest */
      }
    });
  };

  const retry = (why: string) => {
    const h = holder();
    /* Only the CURRENT socket's close may reconnect. A superseded socket's close used to null `ws`
       and schedule another connect, so every orphan's death spawned a replacement. */
    if (h.ws !== socket) {
      log(`orphan closed (${why}) — ignoring`);
      return;
    }
    h.connecting = false;
    h.ws = null;
    reportConnection();
    log(`closed (${why}) — reconnecting in ${h.backoff}ms`);
    h.retryTimer = window.setTimeout(connect, h.backoff);
    h.backoff = Math.min(h.backoff * 2, 15_000);
  };
  socket.onclose = ev => retry(`code ${ev.code}${ev.reason ? ` ${ev.reason}` : ''}`);
  socket.onerror = () => {
    log('error — closing');
    socket.close();
  };
}

/**
 * Heartbeat. Only an UNANSWERED PING counts as death.
 *
 * The earlier rule ("no frame for 60s") killed healthy sockets: builds are quiet between task
 * updates and a backgrounded tab has its timers throttled, so the elapsed gap always looked fatal on
 * return. The server saw those same connections alive for 179s and 239s while rooms climbed to five.
 */
function startHeartbeat(): void {
  const t = holder();
  if (t.heartbeat !== null) return; // an adopted transport already has one running
  t.heartbeat = window.setInterval(() => {
    const h = holder();
    if (!h.ws || h.ws.readyState !== 1) return;
    if (document.hidden) return; // throttled timers make elapsed-time judgements meaningless

    if (h.missedPongs >= MAX_MISSED_PONGS) {
      log(`no answer to ${MAX_MISSED_PONGS} pings — closing`);
      h.missedPongs = 0;
      h.ws.close();
      return;
    }
    try {
      h.missedPongs += 1; // cleared by the next inbound frame of any kind
      h.ws.send(JSON.stringify({ t: 'ping' }));
    } catch {
      /* send on a dying socket; onclose handles it */
    }
  }, PING_EVERY_MS);
}

if (typeof window !== 'undefined') {
  startHeartbeat();

  (window as unknown as { __cbxSocket: () => unknown }).__cbxSocket = () => {
    const t = holder();
    return {
      readyState: t.ws?.readyState ?? 'no socket',
      connecting: t.connecting,
      hasToken: Boolean(getAccessToken()),
      joinedProject: t.currentProject?.projectId ?? null,
      handlers: t.handlers.size,
      missedPongs: t.missedPongs,
      secondsSinceLastFrame: t.lastFrameAt ? Math.round((Date.now() - t.lastFrameAt) / 1000) : null,
    };
  };
}

/*
 * HMR disposal. Without this, a replaced module kept its socket, its reconnect chain and its
 * heartbeat alive forever — the tab accumulated one zombie transport per edit, each rejoining
 * whatever project was open when it was created.
 */
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    const t = holder();
    log('module replaced — disposing transport');
    if (t.heartbeat !== null) window.clearInterval(t.heartbeat);
    if (t.retryTimer !== null) window.clearTimeout(t.retryTimer);
    try {
      t.ws?.close();
    } catch {
      /* already gone */
    }
    delete (window as unknown as Record<string, unknown>)[KEY];
  });
}

/** Join a project room and receive every frame for it. Returns an unsubscribe. */
export function subscribeProject(projectId: string, name: string, handler: Handler): () => void {
  const t = holder();
  t.handlers.add(handler);
  t.currentProject = { projectId, name };
  connect();
  if (t.ws && t.ws.readyState === 1) t.ws.send(JSON.stringify({ t: 'join', projectId, name }));
  return () => {
    holder().handlers.delete(handler);
  };
}

export function sendFrame(frame: Frame): boolean {
  const t = holder();
  if (t.ws && t.ws.readyState === 1) {
    t.ws.send(JSON.stringify(frame));
    return true;
  }
  connect();
  return false;
}

/** Listen to every frame without joining a room (extra consumers on the shared socket). */
export function onFrame(handler: Handler): () => void {
  const t = holder();
  t.handlers.add(handler);
  connect();
  return () => holder().handlers.delete(handler);
}

/** Connection transitions, for the "reconnecting…" note. */
export function onConnectionChange(listener: (open: boolean) => void): () => void {
  const t = holder();
  t.connectionListeners.add(listener);
  listener(Boolean(t.ws && t.ws.readyState === 1));
  return () => holder().connectionListeners.delete(listener);
}

export function socketConnected(): boolean {
  const t = holder();
  return Boolean(t.ws && t.ws.readyState === 1);
}
